import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import get from 'lodash.get'
import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Select,
  Space,
  Form as StyleForm,
  Typography,
  message,
} from 'antd'
import { FieldArray } from 'formik'
import { formOptions, OrderFabricRefPictures } from '../../../forms/blocks'
import { getFixSku } from '../../../network/FixSku'
import { getProductPrices } from '../../../network/ProductPrices'
import { getProductFabric } from '../../../network/ProductFabrics'
import { getFixedDesigns } from '../../../network/FixedDesign'
import { useAuth } from '../../../hooks/useAuth'
import FormContent from '../FormContent'
import {
  getFromKey,
  getFixedDesignCategory,
  getCategoryFromFixedDesign,
  getStockForSelectedSize,
  getRequestedFixSkuQtyForSize,
  selectedOptionsToMap,
  formatSelectedOptions,
  FIXED_DESIGN_OTHER_OPTION_VALUE,
} from '../utils/orderHelpers'
import QrScannerModal from '../../../components/QrScannerModal'
import { SCREEN_SIZE } from '../../../constants/common'
import useIsMobile from '../../../hooks/useIsMobile'

const { Text, Title } = Typography

const Step1 = ({
  values,
  errors,
  handleChange,
  handleBack,
  handleNext,
  validateForm,
  goToPayment,
  canDirectPayment,
  setCanDirectPayment,
  isFastSale = false,
}) => {
  const [isValidated, setIsValidated] = useState(false)
  const [filteredFormOptions, setFilteredFormOptions] = useState({})
  const [currency, setCurrency] = useState('')
  const [shirtFabricOptions, setShirtFabricOptions] = useState([
    { key: 'others', label: 'Others' },
  ])
  const [selectedKey, setSelectedKey] = useState({})
  const [showProductSelection, setShowProductSelection] = useState({}) // Show fixed design list for this product
  const [useCustomInstead, setUseCustomInstead] = useState({}) // When true, hide fixed design card and show "Use fixed design" btn
  const [selectedProduct, setSelectedProduct] = useState({}) // Selected fixed design product by order index
  const [selectedOptions, setSelectedOptions] = useState({}) // Selected options by order index
  const { user } = useAuth()
  const { storeId } = useParams()

  // Fetch all fixed designs once (filter by category happens client-side)
  const fixedDesignsQuery = useQuery(
    [
      'getFixedDesigns',
      {
        sort: [],
        range: [0, 9999],
        filter: { store: storeId },
        tabFilter: {},
      },
    ],
    getFixedDesigns,
    {
      refetchOnWindowFocus: false,
      enabled: !!storeId,
    }
  )

  const allFixedDesigns = get(
    fixedDesignsQuery.data,
    'list',
    get(fixedDesignsQuery.data, 'data.list', Array.isArray(fixedDesignsQuery.data) ? fixedDesignsQuery.data : [])
  )

  useEffect(() => {
    const updateFormOptions = async () => {
      try {
        const data = await getProductPrices()
        // Store the pricing of shirts
        const shirtPricing =
          data.list.products?.find((p) => p.key === 'shirt')?.pricing || []
        window.shirtPricing = shirtPricing

        if (data.list.currency) {
          switch (data.list.currency.toLowerCase()) {
            case 'eur':
              setCurrency(' (in euros)')
              break
            case 'usd':
              setCurrency(' (in dollars)')
              break
            case 'gbp':
              setCurrency(' (in pounds)')
              break
            case 'cny':
              setCurrency(' (in yuan)')
              break
            case 'jpy':
              setCurrency(' (in yen)')
              break
            case 'aud':
              setCurrency(' (in Australian dollars)')
              break
            case 'cad':
              setCurrency(' (in Canadian dollars)')
              break
            case 'chf':
              setCurrency(' (in Swiss francs)')
              break
            case 'hkd':
              setCurrency(' (in Hong Kong dollars)')
              break
            case 'nzd':
              setCurrency(' (in New Zealand dollars)')
              break
            default:
              setCurrency('')
          }
        }

        // set the currency in the order
        values.currency = data.list.currency

        const productKeys = data.list.products.map((price) => price.key)
        const filteredOptions = Object.keys(formOptions)
          .filter((key) => productKeys.includes(key))
          .reduce((acc, key) => {
            const product = data.list.products.find((prod) => prod.key === key)
            if (product) {
              // Put the productPrice in the priceList
              const priceList = product.pricing.reduce((priceAcc, price) => {
                if (priceAcc[price.key]) {
                  console.warn(`Duplicate key found: ${price.key}`)
                }
                priceAcc[price.key] = {
                  price: price.price,
                  label: price.label,
                }
                return priceAcc
              }, {})

              // Update orderCustom object
              const firstPrice = priceList[Object.keys(priceList)[0]] // Get the firt child price
              acc[key] = {
                ...formOptions[key],
                priceList,
                category: product.category?._id || product.category?.objectId || product.category || null,
                default: {
                  ...formOptions[key].default,
                  orderCustom: {
                    ...formOptions[key].default.orderCustom,
                    price: firstPrice ? firstPrice.price : null, // Replace the price
                    quality:
                      product.pricing.length > 0
                        ? product.pricing[0].key
                        : null,
                  },
                },
              }
            }
            return acc
          }, {})

        if (!user.franchiseStore && formOptions.collection) {
          filteredOptions.collection = formOptions.collection
        }

        setFilteredFormOptions(filteredOptions)
      } catch (error) {
        console.error('Failed to fetch product prices:', error)
      }
    }

    updateFormOptions()
  }, [formOptions])

  const handleProductSelection = async (key, index) => {
      if (key === 'shirt') {
        // Fetch fabric options when shirt is selected
        try {
          const fabricResponse = await getProductFabric(key)
          // Create fabric options array
          const fabrics =
            fabricResponse?.product?.fabrics ||
            fabricResponse?.fabrics ||
            fabricResponse?.data?.fabrics ||
            []

          const fabricOptions = [
            ...fabrics.map((fabric) => ({
              key: fabric.key,
              label: fabric.label,
            })),
            { key: 'others', label: 'Others' },
          ]

          // Update the state with fabric options
          setShirtFabricOptions(fabricOptions)

          // Update the form with the default shirt configuration
          const updatedShirtConfig = {
            ...filteredFormOptions[key].default,
            orderCustom: {
              ...filteredFormOptions[key].default.orderCustom,
              // Set fabric options for the form
              fabricOptions: fabricOptions,
            },
          }

          handleChange({
            target: {
              name: `orderProducts.${index}`,
              value: updatedShirtConfig,
            },
          })
        } catch (error) {
          console.error('Error fetching fabric:', error)
          // Still set the default shirt configuration even if fabric fetch fails
          handleChange({
            target: {
              name: `orderProducts.${index}`,
              value: filteredFormOptions[key].default,
            },
          })
        }
      }

      if (key === 'collection' || key === 'adjustment') {
        handleChange({
          target: {
            name: `orderProducts.${index}`,
            value: {
              orderProductType: key,
              [key === 'collection'
                ? 'orderCollection'
                : 'orderAdjustment']: key === 'collection' ? { personaeType: 'special_event' } : {},
            },
          },
        })
      } else {
        handleChange({
          target: {
            name: `orderProducts.${index}`,
            value: filteredFormOptions[key].default,
          },
        })
    }
  }

  // When category has no fixed designs: go straight to custom (no "No fixed designs" step)
  // When category has fixed designs: auto-select the first fixed design by default
  useEffect(() => {
    if (fixedDesignsQuery.isLoading || !allFixedDesigns) return
    values.orderProducts.forEach((_, index) => {
      const key = selectedKey[index]
      if (!key || key === 'collection' || key === 'adjustment') return
      if (!showProductSelection[index]) return
      const filteredList = (allFixedDesigns || []).filter(
        (fd) => getCategoryFromFixedDesign(fd) === key
      )
      if (filteredList.length === 0) {
        if (isFastSale) {
          message.warning('No fixed design available for this product.')
          return
        }
        handleProductSelection(key, index)
        setShowProductSelection((prev) => ({ ...prev, [index]: false }))
      } else if (!selectedProduct[index] && values.orderProducts[index]?.orderProductType !== 'fixed_design' && !useCustomInstead[index]) {
        // Auto-select the first fixed design for this category (but not if user chose custom)
        const product = filteredList[0]
        const priceList = filteredFormOptions[key]?.priceList
        const firstKey = priceList ? Object.keys(priceList)[0] : null
        const initialPrice = firstKey
          ? priceList[firstKey].price
          : (product.retailPrice ?? product.price)
        setSelectedProduct((prev) => ({ ...prev, [index]: product }))
        handleChange({
          target: {
            name: `orderProducts.${index}`,
            value: {
              orderProductType: 'fixed_design',
              orderFixedDesign: {
                productId: product.id,
                productName: product.name,
                sku: product.sku,
                price: initialPrice,
                quality: firstKey ?? undefined,
                deliveryTime: product.deliveryTime,
                description: product.description,
                details: product.details,
                selectedOptions: [],
                personaeType: 'special_event',
              },
            },
          },
        })
      } else {
        console.log("[useEffect auto-select] Skipping auto-select - conditions not met")
      }
    })
  }, [
    fixedDesignsQuery.isLoading,
    allFixedDesigns,
    selectedKey,
    showProductSelection,
    selectedProduct,
    values.orderProducts,
    useCustomInstead,
    isFastSale,
  ])

  // When modifying an order: sync state for existing fixed_design products so detail/price/options show
  useEffect(() => {
    if (fixedDesignsQuery.isLoading || !Array.isArray(allFixedDesigns) || !values.orderProducts?.length) return
    values.orderProducts.forEach((orderProduct, index) => {
      if (orderProduct?.orderProductType !== 'fixed_design') return
      if (useCustomInstead[index]) return
      const category = getFixedDesignCategory(orderProduct)
      if (!category) return
      const productId = get(orderProduct, 'orderFixedDesign.productId')
      if (!productId) return
      // Already synced
      if (selectedProduct[index]?.id === productId || selectedProduct[index]?._id === productId) return
      const product = (allFixedDesigns || []).find(
        (fd) => (fd.id === productId || fd._id === productId)
      )
      if (!product) return
      setSelectedKey((prev) => ({ ...prev, [index]: category }))
      setShowProductSelection((prev) => ({ ...prev, [index]: true }))
      setSelectedProduct((prev) => ({ ...prev, [index]: product }))
      const optionsMap = selectedOptionsToMap(get(orderProduct, 'orderFixedDesign.selectedOptions', []))
      if (Object.keys(optionsMap).length > 0) {
        setSelectedOptions((prev) => ({ ...prev, [index]: optionsMap }))
      }
    })
  }, [fixedDesignsQuery.isLoading, allFixedDesigns, values.orderProducts, useCustomInstead])

  return (
    <>
      <FieldArray name="orderProducts">
        {({ remove, push }) => (
          <Space direction="vertical" style={{ width: '100%' }}>
          {values.orderProducts.length > 0 &&
            values.orderProducts.map((orderProduct, index) => (
              <div key={`${orderProduct.orderProductType}-${index}`}>
                <Space>
                  <Title level={3}>Product {index + 1}</Title>
                  <Button onClick={() => remove(index)} type="link">
                    Delete
                  </Button>
                </Space>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                  }}
                >
                  {Object.keys(filteredFormOptions)
                    .filter((key) => {
                      if (key === 'collection') return true
                      if (key === 'adjustment') return !isFastSale
                      // noCustom products only appear when at least one fixed design exists for that category
                      if (filteredFormOptions[key]?.noCustom) {
                        return (allFixedDesigns || []).some(
                          (fd) => getCategoryFromFixedDesign(fd) === key
                        )
                      }
                      if (!isFastSale) return true
                      return (allFixedDesigns || []).some(
                        (fd) => getCategoryFromFixedDesign(fd) === key
                      )
                    })
                    .map((key) => (
                    <div
                      key={`${key}-${orderProduct.id || index}`}
                      onClick={async () => {
                        setSelectedKey(prev => {
                          const newState = { ...prev }
                          delete newState[index]
                          return newState
                        })

                        const currentProduct = values.orderProducts[index]
                        const currentKey = getFromKey(currentProduct)
                        if (currentKey && currentKey !== key) {
                          handleChange({
                            target: {
                              name: `orderProducts.${index}`,
                              value: { orderProductType: undefined },
                            },
                          })
                        }

                        setShowProductSelection(prev => {
                          const newState = { ...prev }
                          delete newState[index]
                          return newState
                        })
                        setSelectedProduct(prev => {
                          const newState = { ...prev }
                          delete newState[index]
                          return newState
                        })
                        setUseCustomInstead(prev => {
                          const newState = { ...prev }
                          delete newState[index]
                          return newState
                        })

                        if (key === 'collection' || key === 'adjustment') {
                          handleProductSelection(key, index)
                        } else {
                          if (
                            isFastSale &&
                            !(allFixedDesigns || []).some(
                              (fd) => getCategoryFromFixedDesign(fd) === key
                            )
                          ) {
                            message.warning('No fixed design available for this product.')
                            return
                          }
                          setSelectedKey(prev => ({ ...prev, [index]: key }))
                          setShowProductSelection(prev => ({ ...prev, [index]: true }))
                        }
                      }}
                      className={`order-btn auto${
                        getFromKey(values.orderProducts[index]) === key ||
                        selectedKey[index] === key ||
                        (values.orderProducts[index]?.orderProductType === 'fixed_design' &&
                          getFixedDesignCategory(values.orderProducts[index]) === key)
                          ? ' selected'
                          : ''
                      }`}
                    >
                      <Text type="secondary" strong>
                        {filteredFormOptions[key].name}
                      </Text>
                    </div>
                  ))}
                </div>
                {showProductSelection[index] && selectedKey[index] && selectedKey[index] !== 'collection' && selectedKey[index] !== 'adjustment' && (() => {
                  if (fixedDesignsQuery.isLoading) {
                    return (
                      <Card style={{ marginTop: 16, marginBottom: 16 }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Text type="secondary">Loading fixed designs…</Text>
                        </Space>
                      </Card>
                    )
                  }
                  return null
                })()}
                {showProductSelection[index] && selectedKey[index] && selectedKey[index] !== 'collection' && selectedKey[index] !== 'adjustment' && !useCustomInstead[index] && !fixedDesignsQuery.isLoading && (() => {
                  const fixedDesignProductsForCategory = (allFixedDesigns || []).filter(
                    (fd) => getCategoryFromFixedDesign(fd) === selectedKey[index]
                  )
                  return fixedDesignProductsForCategory.length > 0 && (
                  <>
                    <StyleForm component="div" layout="vertical" style={{ width: '100%', marginTop: 16 }}>
                      <Card
                        title={
                          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <span>Fixed designs – {filteredFormOptions[selectedKey[index]]?.name ?? selectedKey[index]}</span>
                            {!isFastSale && !filteredFormOptions[selectedKey[index]]?.noCustom && (
                              <Button
                                type="link"
                                size="small"
                                onClick={() => {
                                  if (selectedKey[index]) {
                                    handleProductSelection(selectedKey[index], index)
                                  }
                                  setUseCustomInstead(prev => ({ ...prev, [index]: true }))
                                  setSelectedProduct(prev => {
                                    const next = { ...prev }
                                    delete next[index]
                                    return next
                                  })
                                }}
                              >
                                Use custom instead
                              </Button>
                            )}
                          </Space>
                        }
                      >
                        <Space
                          size={0}
                          direction="vertical"
                          style={{ width: '100%' }}
                        >
                          <StyleForm.Item label="Fixed design product">
                            <Select
                              style={{ width: '50%' }}
                              placeholder="Select a fixed design product"
                              value={selectedProduct[index]?.id}
                              onChange={(productId) => {
                                const product = fixedDesignProductsForCategory.find(
                                  (p) => (p.id) === productId
                                )
                                setSelectedProduct(prev => ({
                                  ...prev,
                                  [index]: product
                                }))
                                // Reset options when product changes
                                setSelectedOptions(prev => ({
                                  ...prev,
                                  [index]: {}
                                }))
                                
                                // Update form values with fixed design product; price from product-prices when available
                                if (product) {
                                  const priceList = filteredFormOptions[selectedKey[index]]?.priceList
                                  const firstKey = priceList ? Object.keys(priceList)[0] : null
                                  const initialPrice = firstKey
                                    ? priceList[firstKey].price
                                    : (product.retailPrice ?? product.price)
                                  handleChange({
                                    target: {
                                      name: `orderProducts.${index}`,
                                      value: {
                                        orderProductType: 'fixed_design',
                                        orderFixedDesign: {
                                          productId: product.id,
                                          productName: product.name,
                                          sku: product.sku,
                                          price: initialPrice,
                                          quality: firstKey ?? undefined,
                                          deliveryTime: product.deliveryTime,
                                          description: product.description,
                                          details: product.details,
                                          selectedOptions: [],
                                          personaeType: 'special_event'
                                        }
                                      }
                                    }
                                  })
                                }
                              }}
                            >
                              {fixedDesignProductsForCategory.map((product, productIdx) => (
                                <Select.Option key={(product.id) ?? `fd-${index}-${productIdx}`} value={product.id}>
                                  {[product.name?.en || product.name?.zh, product.sku].filter(Boolean).join(' – ')}
                                </Select.Option>
                              ))}
                            </Select>
                          </StyleForm.Item>
                        </Space>
                      </Card>
                    </StyleForm>

                    {/* Price section for fixed design: same selector as custom, from /product-prices */}
                    {filteredFormOptions[selectedKey[index]]?.priceList && (
                      <Card title={'Price' + currency} style={{ marginTop: 16 }}>
                        <Space size={0} direction="vertical" style={{ width: '100%' }}>
                          <StyleForm.Item>
                            <Select
                              style={{ width: '50%' }}
                              placeholder="Select price"
                              value={get(values, `orderProducts.${index}.orderFixedDesign.quality`, undefined)}
                              onChange={(key) => {
                                const priceList = filteredFormOptions[selectedKey[index]].priceList
                                const price = priceList[key]?.price
                                handleChange({
                                  target: {
                                    name: `orderProducts.${index}.orderFixedDesign.quality`,
                                    value: key,
                                  },
                                })
                                handleChange({
                                  target: {
                                    name: `orderProducts.${index}.orderFixedDesign.price`,
                                    value: price ?? 0,
                                  },
                                })
                              }}
                            >
                              {Object.keys(filteredFormOptions[selectedKey[index]].priceList).map((key) => (
                                <Select.Option key={key} value={key}>
                                  {filteredFormOptions[selectedKey[index]].priceList[key].label}
                                </Select.Option>
                              ))}
                            </Select>
                          </StyleForm.Item>
                          <StyleForm.Item
                            validateStatus={
                              get(errors, `orderProducts.${index}.orderFixedDesign.price`, false)
                                ? 'error'
                                : null
                            }
                            help={get(errors, `orderProducts.${index}.orderFixedDesign.price`, null)}
                          >
                            <Input
                              name={`orderProducts.${index}.orderFixedDesign.price`}
                              type="text"
                              placeholder="-"
                              style={{ width: '50%' }}
                              value={get(values, `orderProducts.${index}.orderFixedDesign.price`, '')}
                              disabled
                            />
                          </StyleForm.Item>
                        </Space>
                      </Card>
                    )}

                    {/* Display Product Details */}
                    {selectedProduct[index] && (
                      <Space direction="vertical" style={{ width: '100%', marginTop: 16 }} size="middle">
                        <Card title="Product Details">
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <div>
                              <Text strong>Description: </Text>
                              <Text>{get(selectedProduct[index], 'description.en', get(selectedProduct[index], 'description.zh', ''))}</Text>
                            </div>
                            <div>
                              <Text strong>SKU: </Text>
                              <Text>{selectedProduct[index].sku}</Text>
                            </div>
                          </Space>
                        </Card>

                        {/* Product form fields filtered by _displayFlags - section under Product Details */}
                        {selectedProduct[index] && (() => {
                          const detail0 = selectedProduct[index].details?.[0]
                          const productTypeKey = detail0 && Object.keys(detail0).find((k) => k !== '_id')
                          const productData = productTypeKey ? detail0[productTypeKey] : null
                          const displayFlags = productData?._displayFlags || {}
                          const productForms = formOptions[selectedKey[index]]?.forms
                          if (!productTypeKey || !productForms?.length || Object.keys(displayFlags).length === 0) return null
                          const filterFormByDisplayFlags = (formItem) => {
                            if (!formItem?.sections) return formItem
                            return {
                              ...formItem,
                              sections: formItem.sections
                                .map((section) => ({
                                  ...section,
                                  fields: (section.fields || []).filter((field) => {
                                    const key = field.key?.[0]
                                    return key && displayFlags[key] === true
                                  }),
                                }))
                                .filter((section) => (section.fields || []).length > 0),
                            }
                          }
                          const formGroup = productForms[0]
                          const form = formGroup.form
                          const filteredForm = Array.isArray(form)
                            ? form.map((f) => filterFormByDisplayFlags(f))
                            : filterFormByDisplayFlags(form)
                          let formArray = Array.isArray(filteredForm) ? filteredForm : [filteredForm]
                          // Exclude AddShirtsForm ("Add more shirts") for fixed design
                          formArray = formArray.filter(
                            (f) => f.title !== 'Add more shirts' && !f.sections?.some((s) => (s.key && s.key.join?.('.') === 'extraShirts'))
                          )
                          if (!formArray.some((f) => f.sections?.length > 0)) return null
                          const withShirtOptions = selectedKey[index] === 'shirt'
                            ? formArray.map((f) => ({
                                ...f,
                                sections: f.sections?.map((sec) => ({
                                  ...sec,
                                  fields: sec.fields?.map((field) =>
                                    field.key?.[0] === 'fabricRef' ? { ...field, options: shirtFabricOptions } : field
                                  ),
                                })),
                              }))
                            : formArray
                          const filteredForms = [{
                            ...formGroup,
                            key: `orderFixedDesign.details.0.${productTypeKey}`,
                            form: withShirtOptions,
                          }]
                          return (
                            <div style={{ marginTop: 16, width: '100%' }}>
                              <FormContent
                                haveOldValue={true}
                                values={values}
                                errors={errors}
                                hideErrors={!isValidated}
                                path={`orderProducts.${index}`}
                                handleChange={handleChange}
                                forms={filteredForms}
                              />
                            </div>
                          )
                        })()}

                        {/* Stock warning when selected size has no stock (fixed design) */}
                        {selectedProduct[index] && (() => {
                          const product = selectedProduct[index]
                          const detail0 = product.details?.[0]
                          const productTypeKey = detail0 && Object.keys(detail0).find((k) => k !== '_id')
                          if (!productTypeKey || !Array.isArray(product.stock) || product.stock.length === 0) return null
                          const detail = get(values, `orderProducts.${index}.orderFixedDesign.details.0.${productTypeKey}`, {})
                          const sizeValue = detail.size ?? detail.otherSize ?? detail.shoeSize ?? detail.readyToWearSize ?? detail.tailorMadeSize
                          if (sizeValue == null || sizeValue === '') return null
                          const stock = getStockForSelectedSize(product, sizeValue)
                          if (stock === null || stock > 0) return null
                          return (
                            <Alert
                              type="warning"
                              message="The stock for this size is empty."
                              showIcon
                              style={{ marginTop: 16 }}
                            />
                          )
                        })()}

                        {/* Options: when fixed design has options array */}
                        {selectedProduct[index].options && Array.isArray(selectedProduct[index].options) && selectedProduct[index].options.length > 0 && (
                          <Card title="Options" style={{ marginTop: 16 }}>
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                              {selectedProduct[index].options.map((option, optionIndex) => {
                                const optionLabel = option.name?.en || option.name?.zh || `Option ${optionIndex + 1}`
                                const hasSubOptions = Array.isArray(option.subOptions) && option.subOptions.length > 0
                                return (
                                  <div key={option._id != null ? option._id : `option-${index}-${optionIndex}`}>
                                    <StyleForm.Item 
                                      label={optionLabel}
                                      
                                    >
                                      {option.freeField && !hasSubOptions ? (
                                        <Input
                                          placeholder={`Enter ${optionLabel}`}
                                          value={
                                            selectedOptions[index]?.[`${option._id}__freeFieldValue`] ||
                                            selectedOptions[index]?.[option._id] ||
                                            ''
                                          }
                                          onChange={(e) => {
                                            const newOptionsState = {
                                              ...(selectedOptions[index] || {}),
                                              [option._id]: FIXED_DESIGN_OTHER_OPTION_VALUE,
                                              [`${option._id}__freeFieldValue`]: e.target.value,
                                            }
                                            setSelectedOptions((prev) => ({
                                              ...prev,
                                              [index]: newOptionsState,
                                            }))
                                            const product = selectedProduct[index]
                                            if (product) {
                                              handleChange({
                                                target: {
                                                  name: `orderProducts.${index}.orderFixedDesign.selectedOptions`,
                                                  value: formatSelectedOptions(newOptionsState, product),
                                                },
                                              })
                                            }
                                          }}
                                        />
                                      ) : (
                                        <>
                                          <Select
                                            style={{ width: '100%' }}
                                            placeholder={`Select ${optionLabel}`}
                                            value={selectedOptions[index]?.[option._id]}
                                            onChange={(value) => {
                                              const baseState = {
                                                ...(selectedOptions[index] || {}),
                                                [option._id]: value,
                                              }
                                              // Reset free text when moving away from "Other".
                                              if (value !== FIXED_DESIGN_OTHER_OPTION_VALUE) {
                                                delete baseState[`${option._id}__freeFieldValue`]
                                              }
                                              setSelectedOptions((prev) => ({
                                                ...prev,
                                                [index]: baseState,
                                              }))
                                              const product = selectedProduct[index]
                                              if (product) {
                                                handleChange({
                                                  target: {
                                                    name: `orderProducts.${index}.orderFixedDesign.selectedOptions`,
                                                    value: formatSelectedOptions(baseState, product),
                                                  },
                                                })
                                              }
                                            }}
                                          >
                                            {option.subOptions && option.subOptions.map((subOption, subIndex) => {
                                              const subLabel = subOption.name?.en || subOption.name?.zh || subOption.ref
                                              return (
                                                <Select.Option
                                                  key={subOption._id != null ? subOption._id : `sub-${index}-${optionIndex}-${subIndex}`}
                                                  value={subOption.ref}
                                                >
                                                  {subLabel}{subOption.ref ? ` (Ref: ${subOption.ref})` : ''}
                                                </Select.Option>
                                              )
                                            })}
                                            {option.freeField && (
                                              <Select.Option value={FIXED_DESIGN_OTHER_OPTION_VALUE}>
                                                Other
                                              </Select.Option>
                                            )}
                                          </Select>
                                          {option.freeField && selectedOptions[index]?.[option._id] === FIXED_DESIGN_OTHER_OPTION_VALUE && (
                                            <Input
                                              style={{ marginTop: 8 }}
                                              placeholder={`Enter ${optionLabel}`}
                                              value={selectedOptions[index]?.[`${option._id}__freeFieldValue`] || ''}
                                              onChange={(e) => {
                                                const newOptionsState = {
                                                  ...(selectedOptions[index] || {}),
                                                  [option._id]: FIXED_DESIGN_OTHER_OPTION_VALUE,
                                                  [`${option._id}__freeFieldValue`]: e.target.value,
                                                }
                                                setSelectedOptions((prev) => ({
                                                  ...prev,
                                                  [index]: newOptionsState,
                                                }))
                                                const product = selectedProduct[index]
                                                if (product) {
                                                  handleChange({
                                                    target: {
                                                      name: `orderProducts.${index}.orderFixedDesign.selectedOptions`,
                                                      value: formatSelectedOptions(newOptionsState, product),
                                                    },
                                                  })
                                                }
                                              }}
                                            />
                                          )}
                                        </>
                                      )}
                                    </StyleForm.Item>
                                  </div>
                                )
                              })}
                            </Space>
                          </Card>
                        )}
                      </Space>
                    )}
                  </>
                )})()}
                {getFromKey(values.orderProducts[index]) === 'collection' ? (
                  <CollectionForm
                    values={values}
                    errors={errors}
                    hideErrors={!isValidated}
                    path={`orderProducts.${index}`}
                    handleChange={handleChange}
                  />
                ) : getFromKey(values.orderProducts[index]) === 'adjustment' ? (
                  <AdjustmentForm
                    filteredFormOptions={filteredFormOptions}
                    values={values}
                    errors={errors}
                    hideErrors={!isValidated}
                    path={`orderProducts.${index}`}
                    handleChange={handleChange}
                  />
                ) : (() => {
                  const effectiveProductKey = (values.orderProducts[index]?.orderProductType === 'fixed_design' && useCustomInstead[index])
                    ? selectedKey[index]
                    : getFromKey(values.orderProducts[index])
                  const showCustomBranch =
                    !isFastSale &&
                    effectiveProductKey &&
                    effectiveProductKey !== 'collection' &&
                    effectiveProductKey !== 'adjustment' &&
                    !filteredFormOptions[effectiveProductKey]?.noCustom
                  return showCustomBranch ? (
                  <>
                    {/* Custom price section: show when custom product or when switching to custom (useCustomInstead) */}
                    {(values.orderProducts[index]?.orderProductType !== 'fixed_design' || useCustomInstead[index]) && (
                      <StyleForm component="div" layout="vertical" style={{ width: '100%', marginTop: 16 }}>
                        <Card
                          title={
                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                              <span>{'Price' + currency}</span>
                              {useCustomInstead[index] && selectedKey[index] && selectedKey[index] !== 'collection' && selectedKey[index] !== 'adjustment' && (() => {
                                const fixedDesignProductsForCategory = (allFixedDesigns || []).filter(
                                  (fd) => getCategoryFromFixedDesign(fd) === selectedKey[index]
                                )
                                return fixedDesignProductsForCategory.length > 0 && (
                                  <Button
                                    type="link"
                                    size="small"
                                    onClick={() => setUseCustomInstead((prev) => ({ ...prev, [index]: false }))}
                                  >
                                    Use fixed design instead
                                  </Button>
                                )
                              })()}
                            </Space>
                          }
                        >
                          <Space
                            size={0}
                            direction="vertical"
                            style={{ width: '100%' }}
                          >
                            {filteredFormOptions[effectiveProductKey]?.priceList ? (
                              <StyleForm.Item>
                                <Select
                                  style={{ width: '50%' }}
                                  onChange={(e) => {
                                    handleChange({
                                      target: {
                                        name: `orderProducts.${index}.orderCustom.quality`,
                                        value: e,
                                      },
                                    })
                                    handleChange({
                                      target: {
                                        name: `orderProducts.${index}.orderCustom.price`,
                                        value: filteredFormOptions[effectiveProductKey].priceList[e].price,
                                      },
                                    })
                                  }}
                                  value={get(
                                    values,
                                    `orderProducts.${index}.orderCustom.quality`,
                                    ''
                                  )}
                                >
                                  {Object.keys(filteredFormOptions[effectiveProductKey].priceList).map((key) => (
                                    <Select.Option key={key} value={key}>
                                      {filteredFormOptions[effectiveProductKey].priceList[key].label}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </StyleForm.Item>
                          ) : null}
                          <StyleForm.Item
                            validateStatus={
                              get(
                                errors,
                                `orderProducts.${index}.orderCustom.price`,
                                false
                              )
                                ? 'error'
                                : null
                            }
                            help={get(
                              errors,
                              `orderProducts.${index}.orderCustom.price`,
                              null
                            )}
                          >
                            <Input
                              name={`orderProducts.${index}.orderCustom.price`}
                              type="text"
                              placeholder={'-'}
                              style={{ width: '50%' }}
                              onChange={handleChange}
                              value={get(
                                values,
                                `orderProducts.${index}.orderCustom.price`,
                                ''
                              )}
                              disabled={
                                !formOptions[effectiveProductKey]?.isPriceEditable
                              }
                            />
                          </StyleForm.Item>
                        </Space>
                      </Card>
                    </StyleForm>
                    )}

                    <FormContent
                      haveOldValue={true}
                      values={values}
                      errors={errors}
                      hideErrors={!isValidated}
                      path={`orderProducts.${index}`}
                      handleChange={handleChange}
                      forms={
                        effectiveProductKey === 'shirt'
                          ? formOptions[effectiveProductKey]?.forms?.map((form) => ({
                              ...form,
                              form: form.form?.map((prodForm) => ({
                                ...prodForm,
                                sections: prodForm.sections?.map((section) => ({
                                  ...section,
                                  fields: section.fields?.map((field) => {
                                    // Update fabricRef field with dynamic fabric options
                                    if (field.key?.[0] === 'fabricRef') {
                                      return { ...field, options: shirtFabricOptions }
                                    }
                                    // Update price field in extraShirts section with dynamic price options from /product-prices
                                    if (section.key?.join?.('.') === 'extraShirts' && field.key?.[0] === 'price' && filteredFormOptions[effectiveProductKey]?.priceList) {
                                      const priceList = filteredFormOptions[effectiveProductKey].priceList
                                      const priceOptions = Object.keys(priceList).map((key) => ({
                                        key: key,
                                        label: priceList[key].label,
                                      }))
                                      return {
                                        ...field,
                                        options: priceOptions,
                                        onChange: (value) => {
                                          const selectedPrice = priceList[value]?.price || 0
                                          return selectedPrice
                                        },
                                      }
                                    }
                                    return field
                                  }),
                                })),
                              })),
                            }))
                          : formOptions[effectiveProductKey]?.forms
                      }
                    />
                  </>
                ) : null
                })()}
                {getFromKey(values.orderProducts[index]) && (
                  <>
                    <FormContent
                      haveOldValue={true}
                      values={values}
                      errors={errors}
                      hideErrors={!isValidated}
                      path={`orderProducts.${index}`}
                      handleChange={handleChange}
                      forms={[{ form: [OrderFabricRefPictures], key: '' }]}
                    />
                    <div>
                      <StyleForm component="div" layout="vertical" style={{ width: '100%' }}>
                        <Card title={'Persona Type'} style={{ marginTop: 16, marginBottom: 16 }}>
                        <Space
                          size={0}
                          direction="vertical"
                          style={{ width: '100%' }}
                        ></Space>
                        <Button
                          type={
                            get(
                              values,
                              `orderProducts.${index}.${
                                getFromKey(values.orderProducts[index]) ===
                                'collection'
                                  ? 'orderCollection'
                                  : getFromKey(values.orderProducts[index]) ===
                                    'fixed_design'
                                  ? 'orderFixedDesign'
                                  : 'orderCustom'
                              }.personaeType`
                            ) === 'special_event'
                              ? 'primary'
                              : 'secondary'
                          }
                          onClick={() =>
                            handleChange({
                              target: {
                                name: `orderProducts.${index}.${
                                  getFromKey(values.orderProducts[index]) ===
                                  'collection'
                                    ? 'orderCollection'
                                    : getFromKey(values.orderProducts[index]) ===
                                      'fixed_design'
                                    ? 'orderFixedDesign'
                                    : 'orderCustom'
                                }.personaeType`,
                                value: 'special_event',
                              },
                            })
                          }
                        >
                          Special Event
                        </Button>
                        <Button
                          type={
                            get(
                              values,
                              `orderProducts.${index}.${
                                getFromKey(values.orderProducts[index]) ===
                                'collection'
                                  ? 'orderCollection'
                                  : getFromKey(values.orderProducts[index]) ===
                                    'fixed_design'
                                  ? 'orderFixedDesign'
                                  : 'orderCustom'
                              }.personaeType`
                            ) === 'wardrobe_update'
                              ? 'primary'
                              : 'secondary'
                          }
                          onClick={() =>
                            handleChange({
                              target: {
                                name: `orderProducts.${index}.${
                                  getFromKey(values.orderProducts[index]) ===
                                  'collection'
                                    ? 'orderCollection'
                                    : getFromKey(values.orderProducts[index]) ===
                                      'fixed_design'
                                    ? 'orderFixedDesign'
                                    : 'orderCustom'
                                }.personaeType`,
                                value: 'wardrobe_update',
                              },
                            })
                          }
                        >
                          Wardrobe Update
                        </Button>
                      </Card>
                    </StyleForm>
                  </div>
                  </>
                )}
              </div>
            ))}

          <Space>
            <Button onClick={handleBack} type="secondary">
              Previous
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setCanDirectPayment?.(false)
                push({
                  orderProductType: undefined,
                })
              }}
            >
              Add Product
            </Button>
            <Button
              onClick={() => {
                validateForm().then((res) => {
                  if (Object.keys(res).length) {
                    setIsValidated(true)

                    let t = []
                    while (
                      typeof get(res, t.join('.')) === 'object' ||
                      !t.length
                    ) {
                      let curr = !t.length ? res : get(res, t.join('.'))

                      let kk = Object.keys(curr)
                      t.push(kk[0])
                    }

                    var getMeTo = document.getElementById(t.join('.'))
                    if (!getMeTo) {
                      message.error(
                        'At least a product with a price is required'
                      ) // Avoid the uncaught error if no product are selected
                      return
                    }
                    getMeTo.scrollIntoView({ behavior: 'smooth' }, true)
                  } else {
                    handleNext()
                  }
                })
              }}
              type="secondary"
            >
              Next
            </Button>
            {goToPayment && (
              <Button
                disabled={canDirectPayment === false}
                type="primary"
                onClick={() => {
                  validateForm().then((res) => {
                    if (Object.keys(res).length) {
                      setIsValidated(true)
                      let t = []
                      while (
                        typeof get(res, t.join('.')) === 'object' || !t.length
                      ) {
                        const curr = !t.length ? res : get(res, t.join('.'))
                        const kk = Object.keys(curr)
                        t.push(kk[0])
                      }
                      const getMeTo = document.getElementById(t.join('.'))
                      if (getMeTo) getMeTo.scrollIntoView({ behavior: 'smooth' })
                    } else {
                      goToPayment()
                    }
                  })
                }}
              >
                Payment
              </Button>
            )}
          </Space>
        </Space>
        )}
    </FieldArray>
    </>
  )
}

const AdjustmentForm = ({
  filteredFormOptions,
  values,
  errors,
  path,
  handleChange,
  hideErrors,
}) => {
  const productTypes = Object.keys(filteredFormOptions || {}).map((key) => ({
    key: key,
    label: filteredFormOptions[key].name,
  }))

  const adjustmentForms = formOptions[
    get(values, `${path}.orderAdjustment.type`, '')
  ]?.forms.map((e) => ({
    ...e,
    key: e.key.replace('orderCustom.', ''),
  }))

  const errType = get(errors, `${path}.orderAdjustment.type`, null)

  return (
    <StyleForm component="div" layout="vertical" style={{ width: '100%', marginTop: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Card title="Choose product">
          <StyleForm.Item
            label="Product type"
            validateStatus={errType && !hideErrors ? 'error' : null}
            help={hideErrors ? null : errType}
          >
            <Select
              value={get(values, `${path}.orderAdjustment.type`, '')}
              style={{ width: '50%' }}
              onChange={(id) =>
                handleChange({
                  target: {
                    name: `${path}.orderAdjustment`,
                    value: filteredFormOptions[id].default.orderCustom,
                  },
                })
              }
            >
              {productTypes.map((item) =>
                item.key === 'collection' ||
                item.key === 'adjustment' ? null : (
                  <Select.Option value={item.key}>{item.label}</Select.Option>
                )
              )}
            </Select>
          </StyleForm.Item>
        </Card>
        {get(values, `${path}.orderAdjustment.type`, false) && (
          <FormContent
            values={values}
            errors={errors}
            hideErrors={hideErrors}
            path={`${path}.orderAdjustment`}
            handleChange={handleChange}
            forms={adjustmentForms}
          />
        )}
      </Space>
    </StyleForm>
  )
}

/** Get category key from fix-sku fixedDesign.details[0] (e.g. 'vest', 'belt') */
const getFixSkuCategoryBlocks = (item) => {
  const details = item?.fixedDesign?.details?.[0]
  if (!details || typeof details !== 'object') return []
  const key = Object.keys(details).filter((k) => k !== '_id')[0]
  return key ? [key] : []
}

const CollectionForm = ({ values, errors, path, handleChange, hideErrors }) => {
  const isMobile = useIsMobile(SCREEN_SIZE.MOBILE)
  const [scannerOpen, setScannerOpen] = useState(false)
  const [scanLoading, setScanLoading] = useState(false)

  const products = useQuery(
    [
      'getFixSku',
      {
        sort: [],
        range: [0, 9999],
        filter: {},
        tabFilter: {},
      },
    ],
    getFixSku,
    {
      refetchOnWindowFocus: false,
    }
  )

  const productName = `${path}.orderCollection.product`
  const productDisplayName = `${path}.orderCollection.productName`
  const productType = `${path}.orderCollection.productType`
  const productPrice = `${path}.orderCollection.price`
  const productSize = `${path}.orderCollection.size`
  const productSizesList = `${path}.orderCollection.sizes`
  const product = get(values, productName, null)
  const price = get(values, productPrice, null)
  const list = get(products, 'data.list', [])
  const productSelected = (id) => list.find((e) => e.id === id)

  const errProd = get(errors, `${path}.orderCollection.product`, null)
  const errPrice = get(errors, `${path}.orderCollection.price`, null)
  const errSize = get(errors, `${path}.orderCollection.size`, null)

  const applyProduct = (prod) => {
    handleChange({ target: { name: productName, value: prod.id } })
    handleChange({ target: { name: productType, value: getFixSkuCategoryBlocks(prod) } })
    handleChange({
      target: {
        name: productDisplayName,
        value: `${prod.sku || ''} ${prod.name?.en || prod.name?.zh || ''}`.trim(),
      },
    })
    handleChange({ target: { name: productPrice, value: prod.retailPrice ?? 0 } })
    handleChange({ target: { name: `${path}.orderCollection.personaeType`, value: 'special_event' } })
    handleChange({ target: { name: productSize, value: null } })
    handleChange({ target: { name: productSizesList, value: prod.sizes ?? [] } })
  }

  const handleScan = (text) => {
    let fixSkuId = text.trim()
    try {
      const url = new URL(text)
      const parts = url.pathname.split('/').filter(Boolean)
      const idx = parts.findIndex((p) => p === 'fixed-sku')
      if (idx !== -1 && parts[idx + 1]) fixSkuId = parts[idx + 1]
    } catch {}

    const found = list.find((p) => p.id === fixSkuId)
    if (found) {
      applyProduct(found)
      message.success(`Product "${found.sku || found.name?.en || found.name?.zh}" added`)
      setScannerOpen(false)
    } else {
      message.error('Product not found. Make sure the product list is loaded.')
    }
    setScanLoading(false)
  }

  return (
    <>
    <StyleForm component="div" layout="vertical" style={{ width: '100%', marginTop: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Card title="Choose product">
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
            <Space size={0} direction="vertical" style={{ flex: 1 }}>
              <StyleForm.Item
                label="Product name"
                validateStatus={
                  errProd && !hideErrors
                    ? 'error'
                    : products.isLoading
                    ? 'validating'
                    : null
                }
                help={hideErrors ? null : errProd}
              >
                <Select
                  showSearch
                  optionFilterProp="children"
                  filterOption
                  value={product}
                  style={{ width: isMobile ? '100%' : '50%' }}
                  onChange={(id) => {
                    handleChange({
                      target: {
                        name: productName,
                        value: id,
                      },
                    })

                    handleChange({
                      target: {
                        name: productType,
                        value: getFixSkuCategoryBlocks(productSelected(id)),
                      },
                    })

                    handleChange({
                      target: {
                        name: productDisplayName,
                        value: `${productSelected(id)?.sku} ${
                          productSelected(id)?.name?.en ||
                          productSelected(id)?.name?.zh
                        }`,
                      },
                    })

                    handleChange({
                      target: {
                        name: productPrice,
                        value: productSelected(id)?.retailPrice ?? 0,
                      },
                    })

                    // Set default personaeType to 'special_event'
                    handleChange({
                      target: {
                        name: `${path}.orderCollection.personaeType`,
                        value: 'special_event',
                      },
                    })

                    // Clear size when product changes; store product sizes for validation
                    handleChange({
                      target: {
                        name: productSize,
                        value: null,
                      },
                    })
                    const sizes = productSelected(id)?.sizes ?? []
                    handleChange({
                      target: {
                        name: productSizesList,
                        value: sizes,
                      },
                    })
                  }}
                >
                  {list.map((prod, idx) => (
                    <Select.Option key={prod.id != null ? prod.id : `fixsku-${idx}`} value={prod.id}>
                      {prod.sku ? `${prod.sku} - ` : ''}
                      {prod.name?.en || prod.name?.zh || prod.sku}
                    </Select.Option>
                  ))}
                </Select>
              </StyleForm.Item>
              <Button
                size="small"
                style={{ marginBottom: 8 }}
                onClick={() => setScannerOpen(true)}
              >
                Scan product
              </Button>
              {product && (
                <StyleForm.Item
                  label="Price"
                  validateStatus={errPrice && !hideErrors ? 'error' : null}
                  help={hideErrors ? null : errPrice}
                >
                  <InputNumber
                    min={0}
                    type="tel"
                    name={productPrice}
                    style={{ width: '50%' }}
                    onChange={(e) =>
                      handleChange({
                        target: {
                          name: productPrice,
                          value: e,
                        },
                      })
                    }
                    value={price}
                  />
                </StyleForm.Item>
              )}
            </Space>
            {product && (() => {
              const qrUri = productSelected(product)?.qrCode?.image?.uri
              if (!qrUri) return null
              return (
                <img
                  src={`${process.env.REACT_APP_API_URL}/storage/qr-codes/${qrUri}`}
                  alt="QR Code"
                  style={{ width: 140, height: 140, flexShrink: 0 }}
                />
              )
            })()}
          </div>
        </Card>
        {product && (() => {
          const sizes = productSelected(product)?.sizes ?? []
          const productObj = productSelected(product)
          const selectedSizeObj = get(values, productSize, undefined)
          const stock = productObj && selectedSizeObj && typeof selectedSizeObj === 'object' && selectedSizeObj.value != null
            ? getStockForSelectedSize(productObj, selectedSizeObj)
            : null
          const selectedSizeValue =
            selectedSizeObj != null && typeof selectedSizeObj === 'object'
              ? selectedSizeObj.value
              : selectedSizeObj
          const requestedOther =
            productObj && selectedSizeValue != null && selectedSizeValue !== ''
              ? getRequestedFixSkuQtyForSize({
                  orderProducts: values.orderProducts,
                  productId: productObj.id,
                  selectedSizeValue,
                  excludeIndex: Number(path.split('.').pop()),
                })
              : 0
          const currentLineQtyRaw = get(
            values,
            `${path}.orderCollection.quantity`,
            null
          )
          const currentLineQty =
            typeof currentLineQtyRaw === 'number' &&
            !Number.isNaN(currentLineQtyRaw) &&
            currentLineQtyRaw > 0
              ? currentLineQtyRaw
              : 1
          const requestedQty =
            selectedSizeValue != null && selectedSizeValue !== ''
              ? requestedOther + currentLineQty
              : 0
          return !!sizes?.length && (
          <Card title="Size">
            <Space size={0} direction="vertical" style={{ width: '100%' }}>
              <StyleForm.Item
                label="Size"
                validateStatus={errSize && !hideErrors ? 'error' : null}
                help={hideErrors ? null : errSize}
              >
                <Select
                  placeholder="Select size"
                  value={
                    (() => {
                      const s = get(values, productSize, undefined)
                      return typeof s === 'object' && s?.value != null ? s.value : s
                    })()
                  }
                  style={{ width: '50%' }}
                  onChange={(sizeValue) => {
                    const sizeObj = sizes.find((s) => s.value === sizeValue)
                    const sizeId = sizeObj?._id ?? sizeObj?.id ?? null
                    handleChange({
                      target: {
                        name: productSize,
                        value: sizeObj
                          ? {
                              _id: sizeId,
                              id: sizeId,
                              size_id: sizeId,
                              name: sizeObj.name ?? {},
                              value: sizeObj.value,
                            }
                          : null,
                      },
                    })
                  }}
                >
                  {sizes.map((size, sizeIdx) => (
                    <Select.Option key={size._id != null ? size._id : `size-${sizeIdx}`} value={size.value}>
                      {size.name?.en || size.name?.zh || size.value}
                    </Select.Option>
                  ))}
                </Select>
              </StyleForm.Item>
              {stock !== null && requestedQty > stock && (
                <Alert
                  type="warning"
                  message={`Insufficient stock for this size (available: ${stock}, in this order: ${requestedQty}).`}
                  showIcon
                />
              )}
            </Space>
          </Card>
          )
        })()}
      </Space>
    </StyleForm>
    <QrScannerModal
      open={scannerOpen}
      loading={scanLoading}
      onScan={handleScan}
      onClose={() => {
        setScannerOpen(false)
        setScanLoading(false)
      }}
    />
    </>
  )
}

export default Step1
